/**** OBJECT PAGE ACTIONS ****/

import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    Slide,
    TextField
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {ListViewModel,
        ListViewColumnModel,
        RecordModel,
        ActionModel} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams, useLocation} from 'react-router-dom';
import {getAppName, getObjectName, getRecordUuid } from "../../utils/helperRouter";
import Config from "../../config";
import { useAuth } from "../../hooks/useAuth";
import {ActionComponent} from "./Action";
import { RecordCreateDialogComponent } from "../recordCreateDialog/RecordCreateDialog";
import {CustomIconComponent} from "../icons/CustomIcon";

export const ActionsComponent: React.FC<{
  availableActions: ActionModel[]
}> = ({
  availableActions
}) => {


  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();
  const thisLocation = useLocation();
  const {accessToken, onInvalidToken} = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState<ActionModel | null>(null);
  const [activeComponentData, setActiveComponentData] = useState<any>({});

  const [isPerformingAction, setIsPerformingAction] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(null);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorElement(null);
  };


  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    setErrorMessage(null)
    }
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.style.cursor = isPerformingAction ? 'wait' : 'default';
    return () => {
      document.body.style.cursor='default';
    }

  }, [isPerformingAction])


  const performAction = async (thisAction: ActionModel,
                               authToken: string) => {

    const actionUuid = thisAction.Uuid_sys;
//    const thisRecordUuid = getRecordUuid(recordUuid)
    const thisObjectApiName = getObjectName(objectName)


    try {
      setIsPerformingAction(true);
      setErrorMessage(null);

        //get object information for the selected lookup
      let url = `${Config.getInstance().getCoordinationServiceURL()}/api/x/oaction/${actionUuid}/${thisObjectApiName}`;
      const headers = new Headers();

      headers.append("Authorization", `Bearer ${authToken}`);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");

      const response = await fetch(url, {
          method: "GET",
          headers: headers
        })

        if(!response.ok) {
          const errorData = await response.json();
          if(!errorData.message){
            setErrorMessage("Error when fetching records");
          } else{
            setErrorMessage(errorData.message);
          }
          setIsPerformingAction(false);
          return;
        }

        const data = await response.json();
        if(!data){
            setErrorMessage("Error when fetching data");
            setIsPerformingAction(false);
            return;
        }

        setActiveComponentData(data);
        setSelectedAction(thisAction);
        setIsPerformingAction(false);

    } catch (error){
       setErrorMessage("Unhandled error occurred in Action, please contact your system administrator")
       setActiveComponentData({});
       setSelectedAction(null);
       setIsPerformingAction(false);
    }

  }




  const handleClick = (thisAction: ActionModel) => {
    if(thisAction.OnSuccess_sys.type === 'dialog'){
      performAction(thisAction, accessToken)
      return
    }
    if(thisAction.ApiName_sys === 'Create_c'){
      //special treatement of create action here, for now hardcoded
      setSelectedAction(thisAction)
      return
    }
    setErrorMessage("Redirect action not implemented yet");
    return
  }

  //4 actions being displayed, if more actions existing, drop-down menu
  const NUMBER_ACTION_THRESHOLD = 4

  //navigation bar element (i.e., objects)
  const actionMenuElement = (thisAction: ActionModel) => {
    let buttonSubClass = {my: 2,
                          color: "white",
                          display: "block",
                          backgroundColor: "inherit",
                          borderBottom: "none",
                          borderRadius: 0};
    return (
      <Box key={thisAction.Uuid_sys}>
        <Button
          variant="contained"
          sx={buttonSubClass}
          color="primary"
          fullWidth
          disabled={false}
          onClick={() => handleClick(thisAction)}
        >
          <Typography
            color="textPrimary"
            style={{
              marginTop: 2,
              marginBottom: 2 }}
          >
            {thisAction.Label_sys}
          </Typography>

          {thisAction.ReferencedIcon_sys &&
            <CustomIconComponent
              icon={{...thisAction.ReferencedIcon_sys,
                     Colour_sys: 'text.secondary'}}
              sx={{
                fontSize: 20,
                fontWeight: 500,
                transform: "translateY(-0.075em)"
              }}
            />
          }
        </Button>
      </Box>
    )
  }

  return (
    <>
    {
      selectedAction?.OnSuccess_sys.type === 'dialog' &&
      <ActionComponent
        selectedAction={selectedAction}
        data={activeComponentData}
      />
    }

    {
      selectedAction?.ApiName_sys === 'Create_c' &&
      <RecordCreateDialogComponent
        action = {selectedAction}
        onClose = {() => setSelectedAction(null)}
      />
    }


    {/*All Actions as buttons*/}
    <Box sx={{flexGrow: 1,
              display: { xs: 'none', md: availableActions.length > NUMBER_ACTION_THRESHOLD ? 'none' : 'flex'},
              gap: 1,
              flexWrap: "wrap"}}>
       {availableActions.map((thisAction: ActionModel) => {
         return(
            <Button
               key={thisAction.Uuid_sys}
               color="primary"
               variant="contained"
               disabled={false}
               onClick={() => handleClick(thisAction)}
            >
              {thisAction.Label_sys}
              {thisAction.ReferencedIcon_sys &&
                <CustomIconComponent
                  icon={thisAction.ReferencedIcon_sys}
                  sx={{
                    ml: 2,
                    fontSize: 20,
                    fontWeight: 500,
                    transform: "translateY(-0.075em)"
                  }}
                />
              }
            </Button>
         )
       })
     }
    </Box>



  {/*All Actions as drop-down menu*/}
  <Box
    sx={{
      justifyContent: "flex-end",
      display: { xs: 'flex', md: availableActions.length >= NUMBER_ACTION_THRESHOLD ? 'flex' : 'none'},
      alignItems: "center"
    }}
  >
    <Typography variant="button">
       Actions
    </Typography>
    <IconButton
      size="large"
      onClick={handleOpenMenu}
      color="inherit"
    >
      <ArrowDropDownIcon />
    </IconButton>

    <Menu
      id="menu-actions"
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={Boolean(anchorElement)}
      onClose={handleCloseMenu}
      sx={{ display: { xs: 'block', md: availableActions.length >= NUMBER_ACTION_THRESHOLD ? 'block' : 'none' } }}
    >
       {availableActions.map((thisAction: ActionModel) => {
         return actionMenuElement(thisAction)
       })
     }
    </Menu>
  </Box>


    </>
  );

};
